import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, map, catchError, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ApiService } from '../services/api.service';

// BE-1489: гварды уводят возвратом UrlTree, а не router.navigate. Отдельная навигация кладёт
// новую запись в историю, и «Назад» со входа снова попадает на закрытую страницу, которая
// снова уводит на вход. Редирект из UrlTree роутер при «Назад» и при первой загрузке
// выполняет с заменой адреса. RedirectCommand с replaceUrl здесь не нужен: при переходе по
// ссылке закрытый адрес в историю и так не попадает, а замена затёрла бы страницу-источник.

export const adminGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const authState = authService.authState();
  
  if (!authState.isAuthenticated) {
    return router.createUrlTree(['/auth']);
  }

  if (authService.isAccountant()) {
    return router.createUrlTree(['/accounting/invoices']);
  }
  if (!authService.isAdminContour()) {
    return router.createUrlTree(['/auth/login']);
  }

  return true;
};

export const adminOnlyGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const authState = authService.authState();
  if (!authState.isAuthenticated) {
    return router.createUrlTree(['/auth']);
  }

  if (!authService.isAdmin()) {
    return router.createUrlTree(['/admin/dashboard']);
  }

  return true;
};

/**
 * BE-1252: раздел «Моё вознаграждение» — только роль Менеджер. Кошелёк персональный:
 * у Админа своего нет, он смотрит кошелёк менеджера в его карточке. Скрытый пункт меню
 * от прямого захода по адресу не защищает, поэтому роль проверяется и в маршруте.
 */
export const managerOnlyGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.authState().isAuthenticated) {
    return router.createUrlTree(['/auth']);
  }

  if (!authService.isManager()) {
    return router.createUrlTree(['/admin/dashboard']);
  }

  return true;
};

/**
 * BE-824: страницы сотрудника платформы — админа, root, менеджера и бухгалтера.
 *
 * Отдельный гвард нужен потому, что `adminGuard` уводит бухгалтера на счета раньше любых
 * прочих проверок: с ним бухгалтер не попал бы даже на собственную страницу. Роль берётся
 * из токена, пока не загружен профиль, — иначе прямой заход по ссылке выбрасывал бы
 * сотрудника со своей же страницы.
 */
export const internalUserGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.authState().isAuthenticated) {
    return router.createUrlTree(['/auth']);
  }

  if (!authService.isInternal()) {
    return router.createUrlTree(['/businesses']);
  }

  return true;
};

export const businessGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const authState = authService.authState();
  
  if (!authState.isAuthenticated) {
    return router.createUrlTree(['/auth']);
  }

  if (authService.isAccountant()) {
    return router.createUrlTree(['/accounting/invoices']);
  }
  if (!authService.isBusiness()) {
    return router.createUrlTree(authService.isBusinessUser() ? ['/businesses'] : ['/admin/dashboard']);
  }

  return true;
};

export const businessProfileGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const authState = authService.authState();
  
  if (!authState.isAuthenticated) {
    return router.createUrlTree(['/auth']);
  }

  if (authService.isAccountant()) {
    return router.createUrlTree(['/accounting/invoices']);
  }
  if (authService.isAdminContour()) {
    return router.createUrlTree(['/admin/dashboard']);
  }

  if (authService.isBusiness()) {
    const businessStatus = authState.tokens?.business_status?.toUpperCase();
    if (businessStatus === 'NO_DATA') {
      return router.createUrlTree(['/registration']);
    }
  } else if (authService.isBusinessUser()) {
    return router.createUrlTree(['/businesses']);
  } else {
    return router.createUrlTree(['/auth/login']);
  }

  return true;
};

/** Доступ в бизнес-кабинет: авторизован и не админ/root. */
export const businessCabinetGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const authState = authService.authState();
  if (!authState.isAuthenticated) {
    return router.createUrlTree(['/auth/login']);
  }
  if (authService.isAccountant()) {
    return router.createUrlTree(['/accounting/invoices']);
  }
  if (authService.isAdminContour()) {
    return router.createUrlTree(['/admin/dashboard']);
  }
  if (!authService.isBusinessContour()) {
    return router.createUrlTree(['/auth/login']);
  }
  return true;
};

// BE-807: раздел «Сотрудники» больше не владельческий целиком — его открывает право
// `cabinet.staff.view` (на чтение), а управление составом команды остаётся у владельца
// внутри экрана. Прежний businessEmployeesGuard спрашивал роль отдельным запросом
// /businesses/{id}/check; теперь это делает cabinetPermissionGuard по правам из
// /auth/me — одним механизмом со всеми остальными разделами кабинета.

/** Раздел бухгалтерии: ADMIN/ROOT или ACCOUNTANT. */
export const accountingGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const authState = authService.authState();

  if (!authState.isAuthenticated) {
    return router.createUrlTree(['/auth']);
  }

  if (!authService.isAccountingContour()) {
    return router.createUrlTree(authService.isAdminContour() ? ['/admin/dashboard'] : ['/businesses']);
  }

  return true;
};

/** Проверка доступа к бизнесу по business_id. */
export const businessContextGuard = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): Observable<boolean | UrlTree> => {
  const api = inject(ApiService);
  const router = inject(Router);
  const businessId = route.paramMap.get('business_id');
  if (!businessId) return of(router.createUrlTree(['/businesses']));
  return api.getBusinessesForCurrentUser().pipe(
    map((list) => list.some((b) => b.id === businessId)),
    map((hasAccess) => (hasAccess ? true : router.createUrlTree(['/businesses']))),
    catchError(() => of(router.createUrlTree(['/businesses'])))
  );
};
